/**
 * API client singleton for the applicant portal.
 *
 * Initialises a {@link NoxVerifyClient} once and exposes it for use
 * throughout the portal. The base URL is resolved from the
 * `VITE_API_BASE_URL` environment variable at build time.
 *
 * @module lib/api
 */

import { createNoxVerifyClient, type NoxVerifyClient } from '@noxverify/api-client';

let _client: NoxVerifyClient | null = null;

/** Return the shared API client, creating it on first access. */
export function getApiClient(): NoxVerifyClient {
  if (!_client) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
    _client = createNoxVerifyClient({ baseUrl });
  }
  return _client;
}

// ---------------------------------------------------------------------------
// Signed-URL file upload helper
// ---------------------------------------------------------------------------

/**
 * Upload a file directly to a pre-signed URL using `fetch`.
 *
 * The server returns a signed R2/S3 URL from `requestUploadUrl`. This helper
 * PUTs the raw file bytes to that URL. It must be called *after* obtaining the
 * signed URL and *before* calling `confirmUpload`.
 *
 * @throws {Error} When the PUT response status is not in the 2xx range.
 */
export async function uploadFileToSignedUrl(
  signedUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
  });

  if (!response.ok) {
    throw new Error(
      `File transfer failed (HTTP ${response.status}). Please try again.`,
    );
  }
}
